import {
    useState,
    useEffect,
    Fragment,
    useMemo
} from "react";

import {
    useParams,
    useNavigate
} from "react-router-dom";
import { useSelector } from "react-redux";

import * as hi from "@heroicons/react/24/outline";
import { TbTable } from "react-icons/tb";

import * as t from "../lib/types";
import {
    EndpointRequireConfirmation,
    EndpointRequireConfirmationExampleTags,
    EndpointRequireConfirmationNotifyOnPending,
    EndpointRequireConfirmationNotifyPeriod,
    EndpointType,
    IEndpointOutputNameElement,
    IEndpointOutputNameFieldElement,
    UnitOfProcessing,
    RestApiVersion,
    IEndpointAutomaticTemplateExampleTags,
    ITemplateOnboarding,
    IScrapesRenderConfig,
    IEndpointSplitByField,
    IEndpointAction,
    IConnector,
    EndpointActionType,
    EndpointEmailInputHandling
} from "../lib/backend/extractions.types.generated";
import {
    CONFIRMATION_LOG_LOOKUP_TABLE_HEADER_NAMES,
    ENDPOINT_TYPE,
    ORG_ROLES,
    USER_ROLES
} from "../lib/consts";
import {
    selectEnv,
    selectIsSidebarLarge,
    selectMemberships,
    selectUser
} from "../lib/scraper.slice";
import {
    classNames,
    setDocumentTitle,
    deepCopy,
    unique,
    newUuid
} from "../lib/utils";
import {
    Backend,
    BackendObj
} from "../lib/backend";

import { LoadingSpinner } from "../components/LoadingSpinner";
import { Button } from "../components/Button";
import { Dropdown, } from "../components/Dropdown";
import { ITab, Tabs } from "../components/Tabs";
import { RichDropdown } from "../components/RichDropdown";
import { Textarea, Textbox } from "../components/Textbox";
import { Checkbox } from "../components/Checkbox";
import { EndpointOutputName } from "../components/EndpointOutputName";
import { ConfirmModal } from "../components/ConfirmModal";
import { TwoRowHeader } from "../components/Header";
import {
    EndpointCustomOutputSheets,
    EndpointSplitByField
} from "../components/EndpointCustomOutput";
import { MultiselectInputField } from "../components/MultiselectInputField";
import { DropdownMenu } from "../components/DropdownMenu";
import {
    EditAction,
    getActionDescription,
    getActionType,
    getActionWhen,
    INIT_EMAIL_ACTION,
    INIT_EMAIL_DIGEST_ACTION,
    INIT_ENDPOINT_ACTION,
    INIT_TABLE_UPDATE_ACTION,
    INIT_WEBHOOK_ACTION
} from "../components/Actions";
import { ButtonMenu } from "../components/ButtonMenu";

export interface IEndpointSplitByFieldEx extends t.IEndpointSplitByField {
    context: t.IContextBase;
    field_name: string;
    required: boolean;
}

function NoTemplates() {
    return <div className="hidden lg:fixed lg:left-64 lg:right-0 lg:inset-y-0 lg:flex lg:flex-row">
        <div className="flex justify-center items-center h-screen w-full">
            <div className="text-center">
                <hi.FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-semibold text-gray-900">No templates</h3>
                <p className="mt-1 text-sm text-gray-500">Before you can create Email Connector or REST API you need to create a template.</p>
                <div className="mt-6">
                    <Button icon={hi.FolderPlusIcon} text="Create Template" href="/template/new" />
                </div>
            </div>
        </div>
    </div >;
}

type NewEndpointProps = {
    init_endpoint_type?: EndpointType;
}

export function NewEndpoint({ init_endpoint_type }: NewEndpointProps) {
    const navigate = useNavigate();

    // get redux parameters
    const is_sidebar_large = useSelector(selectIsSidebarLarge);
    const env = useSelector(selectEnv);
    const user = useSelector(selectUser);
    const memberships = useSelector(selectMemberships);
    // get list of admin orgs, since only admin can create or edit templates
    const all_orgs = memberships.map((m) => m.org);
    const admin_orgs = memberships.filter((m) => m.role === ORG_ROLES.admin).map((m) => m.org);
    const is_admin = user.role === USER_ROLES.admin;

    const { endpoint_uuid } = useParams<{ endpoint_uuid: string | undefined }>();

    const is_edit = endpoint_uuid !== undefined;

    // if new endpoint, we are ready to go, else we wait for endpoint to load
    const [is_init, setIsInit] = useState<boolean>(true);
    const [selected_tab_key, setSelectedTab] = useState<string>("basic_info");
    const [is_edit_endpoint_valid, setIsEditEndpointValid] = useState<boolean | undefined>(undefined);
    const [templates, setTemplates] = useState<t.ITemplateBase[] | undefined>(undefined);
    const [lookup_tables, setLookupTables] = useState<t.ILookupTableBase[] | undefined>(undefined);
    const [endpoints, setEndpoints] = useState<t.IEndpointBase[] | undefined>(undefined);
    const [connectors, setConnectors] = useState<IConnector[] | undefined>(undefined);
    const [org_users, setOrgUsers] = useState<t.IOrgUser[]>([]);
    // endpoint details
    const [endpoint_type, setEndpointType] = useState<EndpointType>(init_endpoint_type ?? ENDPOINT_TYPE.email);
    const [endpoint_name, setEndpointName] = useState<string>("");
    const [endpoint_alias, setEndpointAlias] = useState<string>("");
    const [endpoint_user_uuid, setEndpointUserUuid] = useState<string | undefined>(undefined);
    const [endpoint_desc, setEndpointDesc] = useState<string>("");
    // selected template
    const [endpoint_template, setEndpointTemplate] = useState<t.ITemplateBase | undefined>(undefined);
    const [endpoint_template_onboarding, setEndpointTemplateOnboarding] = useState<ITemplateOnboarding | undefined>(undefined);
    const [endpoint_contexts, setEndpointContexts] = useState<t.IContextBase[]>([]);

    const [endpoint_store_extractions, setEndpointStoreExtractions] = useState<boolean>(true);
    const [endpoint_email_input_handling, setEndpointEmailInputHandling] = useState<EndpointEmailInputHandling>("only_attachments_fallback_to_email_body");
    const [endpoint_attachment_filename_regex, setEndpointAttachmentFilenameRegex] = useState<string>("");
    const [endpoint_unit_of_processing, setEndpointUnitOfProcessing] = useState<UnitOfProcessing>("file");
    const [endpoint_automatic_doc_boundary_detection_guidelines, setEndpointAutomaticDocBoundaryDetectionGuidelines] = useState<string>("");
    const [endpoint_truncate_long_text, setEndpointTruncateLongText] = useState<boolean>(false);
    const [endpoint_item_name, setEndpointItemName] = useState<IEndpointOutputNameElement[] | undefined>(undefined);

    const [endpoint_require_confirmation, setEndpointRequireConfirmation] = useState<EndpointRequireConfirmation>("never");
    const [endpoint_require_confirmation_users, setEndpointRequireConfirmationUsers] = useState<string[]>([""]);
    const [endpoint_require_confirmation_notify_on_pending, setEndpointRequireConfirmationNotifyOnPending] = useState<EndpointRequireConfirmationNotifyOnPending>("none");
    const [endpoint_require_confirmation_notify_period, setEndpointRequireConfirmationNotifyPeriod] = useState<EndpointRequireConfirmationNotifyPeriod>("1h");
    const [endpoint_require_confirmation_log_lookup_table_uuid, setEndpointRequireConfirmationLogLookupTableUuid] = useState<string>("");
    const [endpoint_require_confirmation_log_field_uuids, setEndpointRequireConfirmationLogFieldUuids] = useState<IEndpointOutputNameFieldElement[]>([]);
    const [endpoint_require_confirmation_example_tags, setEndpointRequireConfirmationExampleTags] = useState<EndpointRequireConfirmationExampleTags>("none");

    const [endpoint_automatic_template_example, setEndpointAutomaticTemplateExample] = useState<EndpointRequireConfirmation>("never");
    const [endpoint_automatic_template_example_tags, setEndpointAutomaticTemplateExampleTags] = useState<IEndpointAutomaticTemplateExampleTags>({ always: ["Automatic"], confirm: [], reject: [] });

    const [endpoint_actions, setEndpointActions] = useState<IEndpointAction[]>([deepCopy(INIT_EMAIL_ACTION)]);

    const [endpoint_rest_api_version, setEndpointRestApiVersion] = useState<RestApiVersion>("v3");
    const [endpoint_rest_api_check_api_key, setEndpointRestApiCheckApiKey] = useState<boolean>(true);
    const [endpoint_ip_whitelist, setEndpointIpWhitelist] = useState<string[]>([]);
    const [endpoint_rest_api_custom_output_sheets, setEndpointRestApiCustomOutputSheets] = useState<IScrapesRenderConfig[]>([]);
    const [endpoint_rest_api_split_by_field, setEndpointRestApiSplitByField] = useState<IEndpointSplitByField[] | undefined>(undefined);

    // operational state
    const [is_committing, setIsCommitting] = useState<boolean>(false);
    const [show_admin_details, setShowAdminDetails] = useState<boolean>(false);
    const [is_back_modal_open, setIsBackModalOpen] = useState<boolean>(false);
    const [is_creating_lookup_table, setIsCreatingLookupTable] = useState<boolean>(false);
    const [edit_action_idx, setEditActionIdx] = useState<number | undefined>(undefined);
    const [remove_action_idx, setRemoveActionIdx] = useState<number | undefined>(undefined);

    const tabs: ITab[] = [
        { key: "basic_info", name: "General" },
        { key: "rest_api", name: "Rest API", hide: endpoint_type !== ENDPOINT_TYPE.rest_api },
        { key: "actions", name: "Actions" },
        { key: "details", name: "Details" }
    ];

    useEffect(() => {
        BackendObj.extractions.listTemplates({})
            .then(({ templates }) => { setTemplates(templates); })
            .catch(() => { setTemplates([]); });
        BackendObj.extractions.getLookupTables({})
            .then(({ lookup_tables }) => { setLookupTables(lookup_tables); })
            .catch(() => { setLookupTables([]); });
        BackendObj.extractions.listConnectors({})
            .then(({ connectors }) => { setConnectors(connectors); })
            .catch(() => { setConnectors([]); });
        BackendObj.extractions.listEndpoints({})
            .then(({ endpoints }) => { setEndpoints(endpoints); })
            .catch(() => { setEndpoints([]); });
    }, []);

    const org_endpoints = useMemo(() => {
        if (endpoint_template === undefined) { return []; }
        return endpoints?.filter((endpoint) => endpoint.org_uuid === endpoint_template.org_uuid) ?? [];
    }, [endpoint_template, endpoints]);
    const org_connectors = useMemo(() => {
        if (endpoint_template === undefined) { return []; }
        return connectors?.filter((connector) => connector.org_uuid === endpoint_template.org_uuid) ?? [];
    }, [endpoint_template, connectors]);

    useEffect(() => {
        if (endpoint_uuid === undefined) {
            setIsInit(true);
            setEndpointName("");
            setEndpointAlias("");
            setEndpointDesc("");
            setEndpointContexts([]);
            setEndpointActions([deepCopy(INIT_EMAIL_ACTION)]);
        } else {
            BackendObj.extractions.getEndpoint({ endpoint_uuid })
                .then(({ endpoint }) => {
                    if (endpoint === undefined) {
                        setIsEditEndpointValid(false);
                    } else {
                        setIsInit(true);
                        setEndpointType(`${endpoint.type}`);
                        setEndpointName(endpoint.name);
                        setEndpointAlias(endpoint.alias ?? "");
                        // load user only if valid
                        if (endpoint.user) { setEndpointUserUuid(endpoint.user.uuid); }
                        setEndpointDesc(endpoint.description);
                        setEndpointTemplate(endpoint.template);
                        setEndpointContexts(endpoint.template.contexts);

                        setEndpointStoreExtractions(endpoint.details.store_extractions);
                        setEndpointEmailInputHandling(endpoint.details.email_input_handling);
                        setEndpointAttachmentFilenameRegex(endpoint.details.attachment_filename_regex);
                        setEndpointUnitOfProcessing(endpoint.details.unit_of_processing);
                        setEndpointAutomaticDocBoundaryDetectionGuidelines(endpoint.details.automatic_doc_boundary_detection_guidelines);
                        setEndpointTruncateLongText(endpoint.details.truncate_long_text);
                        setEndpointItemName(endpoint.details.item_name.length > 0 ? endpoint.details.item_name : undefined);

                        setEndpointRequireConfirmation(endpoint.details.require_confirmation);
                        setEndpointRequireConfirmationUsers([...endpoint.details.require_confirmation_users, ""]);
                        setEndpointRequireConfirmationNotifyOnPending(endpoint.details.require_confirmation_notify_on_pending);
                        setEndpointRequireConfirmationNotifyPeriod(endpoint.details.require_confirmation_notify_period);
                        setEndpointRequireConfirmationLogLookupTableUuid(endpoint.details.require_confirmation_log_lookup_table_uuid);
                        setEndpointRequireConfirmationLogFieldUuids(endpoint.details.require_confirmation_log_field_uuids);
                        setEndpointRequireConfirmationExampleTags(endpoint.details.require_confirmation_example_tags);

                        setEndpointAutomaticTemplateExample(endpoint.details.automatic_template_example);
                        setEndpointAutomaticTemplateExampleTags(endpoint.details.automatic_template_example_tags);

                        setEndpointActions(endpoint.actions);

                        setEndpointRestApiVersion(endpoint.details.rest_api_version);
                        setEndpointRestApiCheckApiKey(endpoint.details.rest_api_check_api_key);
                        setEndpointIpWhitelist(endpoint.ip_whitelist);
                        setEndpointRestApiCustomOutputSheets(endpoint.details.rest_api_custom_output_sheets);
                        setEndpointRestApiSplitByField(endpoint.details.rest_api_split_by_field);

                        setIsEditEndpointValid(true);
                    }
                })
                .catch((err) => {
                    setIsEditEndpointValid(false);
                    console.log(err);
                });
        }
    }, [endpoint_uuid]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (endpoint_template === undefined) { return; }
        Backend.getOrgUsers({ org_uuid: endpoint_template.org_uuid })
            .then((users) => {
                if (users.length > 0) {
                    // always current user selected first if not yet set
                    if (endpoint_user_uuid === undefined) { setEndpointUserUuid(user.uuid); }
                    // update users
                    setOrgUsers(users);
                } else {
                    setOrgUsers([{ ...user, user_role: user.role, org_role: "admin" }]);
                }
            });
    }, [user, endpoint_template]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (endpoint_template === undefined) { return; }
        BackendObj.extractions.getTemplateOnboarding({ template_uuid: endpoint_template.uuid })
            .then(({ onboarding }) => { setEndpointTemplateOnboarding(onboarding); })
            .catch(() => { setEndpointTemplateOnboarding(undefined); });
    }, [endpoint_template]);

    useEffect(() => {
        if (endpoint_uuid === undefined) {
            setDocumentTitle("New Connector", env);
        } else if (is_edit_endpoint_valid) {
            setDocumentTitle(`Edit Connector - ${endpoint_name}`, env);
        }
    }, [endpoint_uuid, endpoint_name, is_edit_endpoint_valid, env]);

    const changeTemplate = async (template_uuid: string) => {
        // first check if we have selected template and current endpoint name matches template name
        const name_matches = endpoint_template !== undefined && endpoint_template.name === endpoint_name;
        // select template and update contexts
        const { template } = await BackendObj.extractions.getTemplate({ template_uuid });
        if (template === undefined) { return; }
        // if same template, nothing to do
        if (endpoint_template?.uuid === template.uuid) { return; }
        setIsInit(false);
        setEndpointTemplate(template);
        setEndpointContexts(template.contexts);
        // update name only if it was the same as template name or empty
        if (endpoint_name === "" || name_matches) { setEndpointName(template.name); }
        // reset things that have context or field uuids
        setEndpointActions([deepCopy(INIT_EMAIL_ACTION)]);
        // TODO RESET new params on template change
    }

    const onCreateEndpoint = async () => {
        if (endpoint_template === undefined) { return; }
        if (endpoint_user_uuid === undefined) { return; }
        setIsCommitting(true);
        // require confirmation is only for store extractions
        const { endpoint_uuid: new_endpoint_uuid } = await BackendObj.extractions.createEndpoint({
            org_uuid: endpoint_template.org_uuid,
            type: endpoint_type as t.EndpointType,
            name: endpoint_name,
            alias: endpoint_alias === "" ? undefined : endpoint_alias,
            user_uuid: endpoint_user_uuid,
            description: endpoint_desc,
            ip_whitelist: endpoint_ip_whitelist,
            details: {
                store_extractions: endpoint_store_extractions,
                email_input_handling: endpoint_email_input_handling,
                attachment_filename_regex: endpoint_attachment_filename_regex,
                unit_of_processing: endpoint_unit_of_processing,
                automatic_doc_boundary_detection_guidelines: endpoint_automatic_doc_boundary_detection_guidelines,
                truncate_long_text: endpoint_truncate_long_text,
                item_name: endpoint_item_name ?? [],

                require_confirmation: endpoint_require_confirmation,
                require_confirmation_users: endpoint_require_confirmation_users.map((email) => email.trim()).filter((email) => email !== ""),
                require_confirmation_notify_on_pending: endpoint_require_confirmation_notify_on_pending,
                require_confirmation_notify_period: endpoint_require_confirmation_notify_period,
                require_confirmation_log_lookup_table_uuid: endpoint_require_confirmation_log_lookup_table_uuid,
                require_confirmation_log_field_uuids: endpoint_require_confirmation_log_field_uuids,
                require_confirmation_example_tags: endpoint_require_confirmation_example_tags,

                automatic_template_example: endpoint_automatic_template_example,
                automatic_template_example_tags: endpoint_automatic_template_example_tags,

                rest_api_version: endpoint_rest_api_version,
                rest_api_check_api_key: endpoint_rest_api_check_api_key,
                rest_api_custom_output_sheets: endpoint_rest_api_custom_output_sheets,
                rest_api_split_by_field: endpoint_rest_api_split_by_field,
            },
            actions: endpoint_actions,
            template_uuid: endpoint_template.uuid
        });
        if (new_endpoint_uuid !== undefined) {
            navigate(`/endpoint/${new_endpoint_uuid}`);
        }
        setIsCommitting(false);
    };

    const onUpdateEndpoint = async () => {
        if (endpoint_template === undefined) { return; }
        if (endpoint_user_uuid === undefined) { return; }
        setIsCommitting(true);
        if (endpoint_uuid !== undefined) {
            // require confirmation is only for store extractions
            await BackendObj.extractions.updateEndpoint({
                uuid: endpoint_uuid,
                type: endpoint_type as t.EndpointType,
                name: endpoint_name,
                alias: endpoint_alias === "" ? undefined : endpoint_alias,
                org_uuid: endpoint_template.org_uuid,
                user_uuid: endpoint_user_uuid,
                description: endpoint_desc,
                ip_whitelist: endpoint_ip_whitelist,
                details: {
                    store_extractions: endpoint_store_extractions,
                    email_input_handling: endpoint_email_input_handling,
                    attachment_filename_regex: endpoint_attachment_filename_regex,
                    unit_of_processing: endpoint_unit_of_processing,
                    automatic_doc_boundary_detection_guidelines: endpoint_automatic_doc_boundary_detection_guidelines,
                    truncate_long_text: endpoint_truncate_long_text,
                    item_name: endpoint_item_name ?? [],

                    require_confirmation: endpoint_require_confirmation,
                    require_confirmation_users: endpoint_require_confirmation_users.map((email) => email.trim()).filter((email) => email !== ""),
                    require_confirmation_notify_on_pending: endpoint_require_confirmation_notify_on_pending,
                    require_confirmation_notify_period: endpoint_require_confirmation_notify_period,
                    require_confirmation_log_lookup_table_uuid: endpoint_require_confirmation_log_lookup_table_uuid,
                    require_confirmation_log_field_uuids: endpoint_require_confirmation_log_field_uuids,
                    require_confirmation_example_tags: endpoint_require_confirmation_example_tags,

                    automatic_template_example: endpoint_automatic_template_example,
                    automatic_template_example_tags: endpoint_automatic_template_example_tags,

                    rest_api_version: endpoint_rest_api_version,
                    rest_api_check_api_key: endpoint_rest_api_check_api_key,
                    rest_api_custom_output_sheets: endpoint_rest_api_custom_output_sheets,
                    rest_api_split_by_field: endpoint_rest_api_split_by_field
                },
                actions: endpoint_actions,
                template_uuid: endpoint_template.uuid
            });
            navigate(`/endpoint/${endpoint_uuid}`);
        }
    };

    const handleAliasChange = (alias: string) => {
        // make sure alias is either empty or contains only lowercase letters, numbers, and underscores
        if (!/^[a-z0-9_]*$/.test(alias)) { return; }
        setIsInit(false);
        setEndpointAlias(alias);
    }

    const addConfirmationLogFieldElement = (element: IEndpointOutputNameElement) => {
        setIsInit(false);
        const new_element = { ...element };
        if (new_element.type === "field") {
            setEndpointRequireConfirmationLogFieldUuids([...endpoint_require_confirmation_log_field_uuids, new_element]);
        }
    }

    const removeConfirmationLogFieldElement = (element_idx: number) => {
        setIsInit(false);
        const new_elements = [...(endpoint_require_confirmation_log_field_uuids || [])];
        new_elements.splice(element_idx, 1);
        setEndpointRequireConfirmationLogFieldUuids(new_elements);
    }

    const addAutomaticTemplateExampleTag = (key: "confirm" | "reject" | "always", tag: string) => {
        setIsInit(false);
        const new_tags = unique([...(endpoint_automatic_template_example_tags[key]), tag]);
        setEndpointAutomaticTemplateExampleTags({ ...endpoint_automatic_template_example_tags, [key]: new_tags });
    }

    const removeAutomaticTemplateExampleTag = (key: "confirm" | "reject" | "always", tag_idx: number) => {
        setIsInit(false);
        const remove_tag = endpoint_automatic_template_example_tags[key][tag_idx];
        const new_tags = endpoint_automatic_template_example_tags[key].filter((t) => t !== remove_tag);
        setEndpointAutomaticTemplateExampleTags({ ...endpoint_automatic_template_example_tags, [key]: new_tags });
    }

    const updateRestApiSplitByField = (new_split_by_field: IEndpointSplitByField[] | undefined) => {
        setIsInit(false);
        const is_empty = new_split_by_field === undefined || new_split_by_field.length === 0;
        setEndpointRestApiSplitByField(is_empty ? undefined : new_split_by_field);
    }

    const updateRestApiCustomOutputSheets = (custom_output_sheets: IScrapesRenderConfig[]) => {
        setIsInit(false);
        setEndpointRestApiCustomOutputSheets(custom_output_sheets);
    }

    const addAction = (type: EndpointActionType) => {
        setIsInit(false);
        const new_action = type === "email" ? deepCopy(INIT_EMAIL_ACTION) :
            type === "email_digest" ? deepCopy(INIT_EMAIL_DIGEST_ACTION) :
                type === "endpoint" ? deepCopy(INIT_ENDPOINT_ACTION) :
                    type === "webhook" ? deepCopy(INIT_WEBHOOK_ACTION) :
                        deepCopy(INIT_TABLE_UPDATE_ACTION);
        const new_action_idx = endpoint_actions.length;
        setEndpointActions([...endpoint_actions, new_action]);
        setEditActionIdx(new_action_idx);
    }

    const refreshConnectors = () => {
        BackendObj.extractions.listConnectors({ org_uuid: endpoint_template?.org_uuid })
            .then(({ connectors }) => { setConnectors(connectors); });
    }

    const updateAction = (action_idx: number, updated_action?: IEndpointAction) => {
        if (updated_action !== undefined) {
            setIsInit(false);
            setEndpointActions((prev) => prev.map((a, idx) => idx === action_idx ? updated_action : a));
        }
        setEditActionIdx(undefined);
    }

    const removeAction = (result: boolean, action_idx: number) => {
        setIsInit(false);
        if (result) {
            setEndpointActions((prev) => prev.filter((_, idx) => idx !== action_idx));
        }
        setRemoveActionIdx(undefined);
    }

    const handleConfirmationUserChange = (idx: number, email: string) => {
        setIsInit(false);
        let new_users = [...endpoint_require_confirmation_users];
        new_users[idx] = email;
        new_users = new_users.filter((email) => email.trim() !== "");
        new_users = [...new Set(new_users)];
        new_users.push("");
        setEndpointRequireConfirmationUsers(new_users);
    }

    const handleAddLookupTable = async () => {
        if (endpoint_template === undefined) { return; }
        setIsInit(false);
        // create new lookup table
        setIsCreatingLookupTable(true);
        const new_lookup_table: Omit<t.ILookupTableBase, "uuid"> = {
            org_uuid: endpoint_template.org_uuid,
            name: `Validation results for ${endpoint_name}`,
            headers: CONFIRMATION_LOG_LOOKUP_TABLE_HEADER_NAMES.map((name) => ({ uuid: newUuid(), name })),
            created_at: Date.now(),
            details: {
                merge_key_column_uuids: [],
                rest_api_update: false,
                rest_api_get: false
            }
        };
        const { lookup_table_uuid } = await BackendObj.extractions.createLookupTable(new_lookup_table);
        if (lookup_table_uuid === undefined) {
            setIsCreatingLookupTable(false);
            return;
        }
        // add new lookup table to the list
        setLookupTables([...(lookup_tables || []), { uuid: lookup_table_uuid, ...new_lookup_table }]);
        // set new lookup table as confirmation log lookup table
        setEndpointRequireConfirmationLogLookupTableUuid(lookup_table_uuid);
        setIsCreatingLookupTable(false);
    }

    const addItemNameElement = (element: IEndpointOutputNameElement) => {
        setIsInit(false);
        const new_element = { ...element };
        if (endpoint_item_name === undefined) {
            setEndpointItemName([new_element]);
        } else {
            setEndpointItemName([...endpoint_item_name, new_element]);
        }
    }

    const removeItemNameElement = (element_idx: number) => {
        setIsInit(false);
        const new_elements = [...(endpoint_item_name || [])];
        new_elements.splice(element_idx, 1);
        setEndpointItemName(new_elements);
    }

    const handleCheckBack = () => {
        if (is_init) {
            navigate(endpoint_uuid ? `/endpoint/${endpoint_uuid}` : "/endpoints");
        } else {
            setIsBackModalOpen(true);
        }
    }

    const handleBack = (result: boolean) => {
        if (result) {
            navigate(endpoint_uuid ? `/endpoint/${endpoint_uuid}` : "/endpoints");
        }
        setIsBackModalOpen(false);
    }

    // when we are checking there if we have contexts, show spinner
    // if undefined we are still loading
    if (templates === undefined) {
        return <div className={classNames("hidden lg:fixed lg:right-0 lg:inset-y-0 lg:flex lg:flex-row", is_sidebar_large ? "lg:left-64" : "lg:left-20")}>
            <LoadingSpinner />
        </div>;
    }

    // if we open in edit mode, check that we have valid context and that it is loaded
    if (is_edit) {
        if (is_edit_endpoint_valid === undefined) {
            return <div className={classNames("hidden lg:fixed lg:right-0 lg:inset-y-0 lg:flex lg:flex-row", is_sidebar_large ? "lg:left-64" : "lg:left-20")}>
                <LoadingSpinner />
            </div>;
        }

        if (!is_edit_endpoint_valid) {
            return <div className="px-6 py-4">
                <div className="py-10">
                    <h2 className="text-base font-semibold leading-7 text-gray-900">Invalid Connector</h2>
                    <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-600">
                        Connector does not exist.
                    </p>
                </div>
            </div>;
        }
    }

    if (templates.length === 0) {
        return <NoTemplates />;
    }

    const is_invalid = endpoint_name === "" || endpoint_contexts.length === 0;
    const is_not_admin_in_all_orgs = admin_orgs.length !== all_orgs.length;
    const admin_templates = templates.filter((template) => admin_orgs.find((org) => org.uuid === template.org_uuid));

    // filter lookup tables to only include those that are from the same org as the endpoint
    const org_lookup_tables = (lookup_tables || []).filter((lookup_table) => lookup_table.org_uuid === endpoint_template?.org_uuid);

    // prepare Save template tooltip:
    let save_tooltip: string | undefined = undefined;
    if (endpoint_name === "") {
        save_tooltip = "Please provide a name for the connector";
    } else if (endpoint_contexts.length === 0) {
        save_tooltip = "Please select at least one template";
    } else if (is_edit && !is_init) {
        save_tooltip = "Unsaved changes";
    }

    const endpoint_org = memberships.find(({ org }) => org.uuid === endpoint_template?.org_uuid);

    const green_tags = endpoint_template_onboarding?.step === "build" ? endpoint_template_onboarding.build.tags.green_tags :
        endpoint_template_onboarding?.step === "onboarding" ? (endpoint_template_onboarding.onboarding?.tags.green_tags ?? []) :
            endpoint_template_onboarding?.step === "operation" ? (endpoint_template_onboarding.operation?.tags.green_tags ?? []) :
                [];
    const red_tags = endpoint_template_onboarding?.step === "build" ? endpoint_template_onboarding.build.tags.red_tags :
        endpoint_template_onboarding?.step === "onboarding" ? (endpoint_template_onboarding.onboarding?.tags.red_tags ?? []) :
            endpoint_template_onboarding?.step === "operation" ? (endpoint_template_onboarding.operation?.tags.red_tags ?? []) :
                [];
    const blue_tags = ["Automatic", "Build", "Onboarding", "New Example"];
    // name of endpoint is also possible tag for examples, to distinguish between examples with the same template
    blue_tags.push(endpoint_name);

    return <div className={classNames("flex-row lg:fixed lg:right-0 lg:inset-y-0 overflow-y-auto", is_sidebar_large ? "lg:left-64" : "lg:left-20")}>
        <TwoRowHeader
            title={endpoint_name.length > 0 ? endpoint_name : "<no name>"}
            subtitle={`${is_edit ? "Edit" : "New"} ${endpoint_type === ENDPOINT_TYPE.email ? "Email Connector" : "Rest API"} `}
            org_name={endpoint_org?.org.name}
            buttons={<Fragment>
                {!is_edit && <Button
                    text="Create"
                    highlight={true}
                    disabled={is_invalid || is_committing}
                    loading={is_committing}
                    onClick={onCreateEndpoint} />}
                {is_edit && <Button
                    text="Save Changes"
                    highlight={true}
                    disabled={is_invalid || is_committing || is_init}
                    loading={is_committing}
                    onClick={onUpdateEndpoint} />}
            </Fragment>}
            tooltip={save_tooltip}
            onBack={handleCheckBack}
        />
        <ConfirmModal
            open={is_back_modal_open}
            title="Unsaved changes"
            message={["There are unsaved changes to the connector.", "Are you sure you want to go back?"]}
            cancel="No"
            confirm="Yes"
            onClose={handleBack} />

        <div className="p-8 py-6 max-w-5xl">
            <Tabs tabs={tabs} selected_tab_key={selected_tab_key} setSelectedTab={setSelectedTab} />
        </div>

        {selected_tab_key === "basic_info" && <div className="px-10 max-w-5xl">
            <form>
                <div className="py-6 sm:grid sm:grid-cols-3 max-w-5xl sm:items-start sm:gap-4">
                    <label htmlFor="endpoint_name" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                        Name
                    </label>
                    <div className="mt-2 sm:col-span-2 sm:mt-0">
                        <Textbox
                            value={endpoint_name}
                            onChange={(value) => { setIsInit(false); setEndpointName(value) }}
                        />
                    </div>
                </div>

                <div className="pb-6 sm:grid sm:grid-cols-3 max-w-5xl sm:items-start sm:gap-4">
                    <label htmlFor="endpoint_alias" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                        Alias
                    </label>
                    <div className="mt-2 sm:col-span-2 sm:mt-0">
                        <Textbox
                            value={endpoint_alias}
                            disabled={!is_admin}
                            onChange={(value) => { handleAliasChange(value) }}
                        />
                    </div>
                </div>

                <div className="sm:grid sm:grid-cols-3 max-w-5xl sm:items-start sm:gap-4 sm:py-6 border-t border-gray-900/10">
                    <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                        <p>Template</p>
                        {!is_edit && <p className="mt-3 max-w-2xl text-sm leading-6 font-normal text-gray-600">
                            Select template you want to include in this connector.
                        </p>}
                        {is_not_admin_in_all_orgs && <p className="mt-3 max-w-2xl text-sm leading-6 font-normal text-gray-600">
                            Only templates from organizations where you are an admin are available.
                        </p>}
                    </label>
                    <div className="mt-2 sm:col-span-2 sm:mt-0">
                        <RichDropdown
                            values={[
                                { id: "", name: "/", icon: TbTable },
                                ...admin_templates.map((template) => ({
                                    id: template.uuid,
                                    name: template.name,
                                    icon: TbTable,
                                    org: all_orgs.find((org) => org.uuid === template.org_uuid),
                                    ts: template.created_at
                                }))
                            ]}
                            disabled={is_edit}
                            selected={endpoint_template?.uuid ?? ""}
                            onChange={(uuid) => changeTemplate(uuid)} />
                        <div className="py-2 text-sm text-gray-600">
                            {endpoint_contexts.map((context, idx) => <Fragment key={idx}>
                                <p className="font-semibold pt-4">Sheet {idx + 1}: {context.name}</p>
                                <p className="py-2">{context.fields.map((field) => field.name).join(", ")}</p>
                            </Fragment>)}
                        </div>
                    </div>
                </div>
            </form>
        </div>}

        {selected_tab_key === "actions" && <div className="px-10 max-w-5xl">
            <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-3 py-2 text-left text-sm font-semibold text-gray-900 w-1"></th>
                            <th scope="col" className="px-3 py-2 text-left text-sm font-semibold text-gray-900">Type</th>
                            <th scope="col" className="px-3 py-2 text-left text-sm font-semibold text-gray-900">When</th>
                            <th scope="col" className="px-3 py-2 text-left text-sm font-semibold text-gray-900">Parameters</th>
                            <th className="px-3 py-2 w-32 text-right">
                                <ButtonMenu title="Add" items={[
                                    { title: "Email", onClick: () => addAction("email") },
                                    { title: "Email Digest", onClick: () => addAction("email_digest") },
                                    { title: "Trigger workflow", onClick: () => addAction("endpoint") },
                                    { title: "Webhook", onClick: () => addAction("webhook") },
                                    { title: "Table Update", onClick: () => addAction("table_update") }
                                ]} />
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                        {endpoint_actions.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="px-6 py-8 text-center text-gray-400 text-sm">
                                    No actions configured yet.
                                </td>
                            </tr>
                        ) : (
                            endpoint_actions.map((action, idx) => (
                                <tr key={idx} className="transition">
                                    <td className="px-4 py-4 text-sm text-gray-500 align-top">
                                        <div className={classNames(`relative h-5 w-5 rounded-full flex items-center justify-center`, action.status === "active" ? "bg-mint-100" : "bg-gray-200")}>
                                            <div className={classNames(`absolute h-3 w-3 rounded-full`, action.status === "active" ? "bg-mint-400" : "bg-gray-400")} title={action.status === "active" ? "Active" : "Disabled"} />
                                        </div>
                                    </td>
                                    <td className="px-3 py-4 text-sm text-gray-500 align-top">
                                        {getActionType(action)}
                                    </td>
                                    <td className="px-3 py-4 text-sm text-gray-500 align-top">
                                        {getActionWhen(action)}
                                    </td>
                                    <td className="px-3 py-4 text-sm text-gray-500 align-top">
                                        {getActionDescription(action, org_connectors, org_endpoints, org_lookup_tables, endpoint_contexts)}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500 align-top">
                                        <Button icon={hi.PencilIcon} onClick={() => setEditActionIdx(idx)} />
                                        <Button icon={hi.TrashIcon} onClick={() => setRemoveActionIdx(idx)} />
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {edit_action_idx !== undefined && endpoint_template && <EditAction
                org_users={org_users}
                template={endpoint_template}
                contexts={endpoint_contexts}
                connectors={org_connectors}
                endpoints={org_endpoints}
                lookup_tables={org_lookup_tables}
                action={endpoint_actions[edit_action_idx]}
                refreshConnectors={refreshConnectors}
                onClose={(updated_action) => updateAction(edit_action_idx, updated_action)}
            />}
            {remove_action_idx !== undefined && <ConfirmModal
                open={remove_action_idx !== undefined}
                title="Remove action"
                message={["Are you sure you want to remove this action?"]}
                cancel="No"
                confirm="Yes"
                onClose={(result) => removeAction(result, remove_action_idx)} />}
        </div>}

        {selected_tab_key === "rest_api" && <div className="px-10 max-w-5xl">
            <div className="sm:grid sm:grid-cols-3 max-w-5xl sm:items-start sm:gap-4 pb-6">
                <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                    <p>Owner</p>
                    <p className="mt-3 max-w-2xl text-sm leading-6 font-normal text-gray-600">
                        When endpoint is called via Rest API, owner is the user that will be used for logging the call and storing results.
                    </p>
                </label>
                <div className="flex gap-2 mt-2 sm:col-span-2 sm:mt-0 max-w-2xl">
                    <Dropdown
                        values={org_users.map((user) => `${user.first_name} ${user.last_name} (${user.email})`)}
                        ids={org_users.map((user) => user.uuid)}
                        selected={endpoint_user_uuid ?? ""}
                        onChange={(uuid) => { setIsInit(false); setEndpointUserUuid(uuid) }} />
                </div>
            </div>

            <div className="sm:grid sm:grid-cols-6 sm:items-start sm:gap-4 py-6 border-t border-gray-900/10">
                <label htmlFor="endpoint_rest_api_version" className="block sm:col-span-2 text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                    <p>REST API version</p>
                </label>
                <div className="mt-2 sm:mt-0">
                    <Dropdown
                        values={["v1", "v2", "v3"]}
                        ids={["v1", "v2", "v3"]}
                        selected={endpoint_rest_api_version}
                        onChange={(version) => { setIsInit(false); setEndpointRestApiVersion(version as RestApiVersion); }} />
                </div>
                <label htmlFor="endpoint_rest_api_check_api_key" className="block sm:col-span-2 text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                    <p>Check API key</p>
                </label>
                <div className="mt-2 sm:mt-0">
                    <Checkbox
                        id="endpoint_rest_api_check_api_key"
                        checked={endpoint_rest_api_check_api_key}
                        setChecked={(new_value) => { setIsInit(false); setEndpointRestApiCheckApiKey(new_value); }}
                    />
                </div>
            </div>

            <div className="py-6 border-t border-gray-900/10">
                <EndpointSplitByField
                    prefix="rest_api"
                    in_a_box={false}
                    contexts={endpoint_contexts}
                    split_by_field={endpoint_rest_api_split_by_field}
                    is_committing={is_committing}
                    tooltip="You can split the result into multiple individual results by selecting one or more
                        split fields. A separate result is created for each unique combination of the split fields.
                        Rest API will return array of all the results."
                    updateSplitByField={(split_by_field) => updateRestApiSplitByField(split_by_field)}
                />
            </div>

            <div className="py-6 border-t border-gray-900/10">
                <EndpointCustomOutputSheets
                    prefix="rest_api"
                    in_a_box={false}
                    contexts={endpoint_contexts}
                    custom_output_sheets={endpoint_rest_api_custom_output_sheets}
                    split_by_field={endpoint_rest_api_split_by_field}
                    is_committing={is_committing}
                    tooltip="By default all the fields from the template are included in the result.
                        If you want to customize the output, you can do it here. Just list the sheets and columns you
                        want to include in the result. Sheets and columns will correspond to keys in the JSON response.
                        Fields with note 'multiple values possible' can have multiple values, and only the first value will
                        be used for the output."
                    updateOutputSheets={(output_sheets) => updateRestApiCustomOutputSheets(output_sheets)}
                />
            </div>

        </div>}

        {selected_tab_key === "details" && <div className="px-10 max-w-5xl">
            <form>
                <div className="sm:grid sm:grid-cols-4 sm:items-start sm:gap-4 py-6 border-b border-gray-900/10">
                    <label htmlFor="endpoint_require_confirmation" className="block text-sm font-medium sm:col-span-3 leading-6 text-gray-900 sm:pt-1.5">
                        <p>Require Manual Confirmation</p>
                        <p className="mt-3 max-w-2xl text-sm leading-6 font-normal text-gray-600">
                            Should we require manual confirmation before sending the data to the webhook or email.
                        </p>
                    </label>
                    <div className="mt-2 sm:mt-0">
                        <Dropdown
                            values={["No", "Yes", "Only on error"]}
                            ids={["never", "always", "on_error"]}
                            selected={endpoint_require_confirmation}
                            onChange={(type) => { setIsInit(false); setEndpointRequireConfirmation(type as EndpointRequireConfirmation) }} />
                    </div>
                    {endpoint_require_confirmation !== "never" && <div className="sm:col-span-4 grid grid-cols-4 gap-4">
                        <label htmlFor="endpoint_require_confirmation_notify_on_pending" className="block text-sm font-medium sm:col-span-3 leading-6 text-gray-900 sm:pt-1.5">
                            <p>Notify on Pending</p>
                            <p className="mt-3 max-w-2xl text-sm leading-6 font-normal text-gray-600">
                                Who should receive email notifications when processing requires confirmation and is pending.
                            </p>
                        </label>
                        <div className="mt-2 sm:mt-0">
                            <Dropdown
                                values={["Nobody", "Confirmation users", "Sender", "All"]}
                                ids={["none", "users", "sender", "all"]}
                                selected={endpoint_require_confirmation_notify_on_pending}
                                onChange={(type) => { setIsInit(false); setEndpointRequireConfirmationNotifyOnPending(type as EndpointRequireConfirmationNotifyOnPending) }} />
                        </div>
                        {endpoint_require_confirmation_notify_on_pending !== "none" && <Fragment>
                            <label htmlFor="endpoint_require_confirmation_notify_period" className="block text-sm font-medium sm:col-span-3 leading-6 text-gray-900 sm:pt-1.5">
                                <p>Notification period</p>
                                <p className="mt-3 max-w-2xl text-sm leading-6 font-normal text-gray-600">
                                    How often should we notify the confirmation users about the pending confirmation.
                                </p>
                            </label>
                            <div className="mt-2 sm:mt-0">
                                <Dropdown
                                    values={["Immediately", "1 hour", "4 hours", "8 hours", "12 hours", "24 hours"]}
                                    ids={["per_job", "1h", "4h", "8h", "12h", "24h"]}
                                    selected={endpoint_require_confirmation_notify_period}
                                    onChange={(type) => { setIsInit(false); setEndpointRequireConfirmationNotifyPeriod(type as EndpointRequireConfirmationNotifyPeriod) }} />
                            </div>
                        </Fragment>}
                        <label htmlFor="endpoint_require_confirmation_users" className="block text-sm font-medium sm:col-span-2 leading-6 text-gray-900 sm:pt-1.5">
                            <p>Confirmation Users</p>
                            <p className="mt-3 max-w-2xl text-sm leading-6 font-normal text-gray-600">
                                Users that will receive the confirmation request.<br /> Senders always receive the confirmation request.
                            </p>
                        </label>
                        <div className="sm:col-span-2 flex flex-col gap-4">
                            {endpoint_require_confirmation_users.map((user_email, idx) => (
                                <Dropdown
                                    key={idx}
                                    values={["/", ...org_users.map((user) => `${user.first_name} ${user.last_name} (${user.email})`)]}
                                    ids={["", ...org_users.map((user) => user.email)]}
                                    selected={user_email}
                                    onChange={(email) => { setIsInit(false); handleConfirmationUserChange(idx, email) }} />
                            ))}
                        </div>
                        <label htmlFor="endpoint_require_confirmation_notify_on_pending" className="block text-sm font-medium sm:col-span-2 leading-6 text-gray-900 sm:pt-1.5">
                            <p>Log Validation Results</p>
                            <p className="mt-3 max-w-2xl text-sm leading-6 font-normal text-gray-600">
                                Store results of validation to a lookup table.
                            </p>
                        </label>
                        <div className="mt-2 sm:mt-0 sm:col-span-2 flex flex-row gap-x-2 items-center">
                            <Dropdown
                                values={["/", ...org_lookup_tables.map((lookup_table) => lookup_table.name)]}
                                ids={["", ...org_lookup_tables.map((lookup_table) => lookup_table.uuid)]}
                                selected={endpoint_require_confirmation_log_lookup_table_uuid}
                                disabled={is_creating_lookup_table}
                                onChange={(uuid) => { setIsInit(false); setEndpointRequireConfirmationLogLookupTableUuid(uuid) }} />
                            <Button icon={hi.PlusIcon} onClick={handleAddLookupTable} disabled={is_creating_lookup_table} />
                        </div>
                        {endpoint_require_confirmation_log_lookup_table_uuid !== "" && <Fragment>
                            <div className="sm:col-span-2" />
                            <div className="mt-2 sm:mt-0 sm:col-span-2 flex flex-row gap-x-2 items-center">
                                <EndpointOutputName
                                    title={"Field values to track in log"}
                                    contexts={endpoint_contexts}
                                    split_fields_data={[]}
                                    elements={endpoint_require_confirmation_log_field_uuids}
                                    placeholder="No fields selected"
                                    addElement={addConfirmationLogFieldElement}
                                    removeElement={removeConfirmationLogFieldElement}
                                    separator="space"
                                />
                            </div>
                        </Fragment>}
                        <label htmlFor="endpoint_require_confirmation_example_tags" className="block text-sm font-medium sm:col-span-3 leading-6 text-gray-900 sm:pt-1.5">
                            <p>Require Example and Error Report Tags</p>
                            <p className="mt-3 max-w-2xl text-sm leading-6 font-normal text-gray-600">
                                Require tags to be added to the example or error report.
                            </p>
                        </label>
                        <div className="mt-2 sm:mt-0 flex flex-row gap-x-2 items-center">
                            <Dropdown
                                values={["None", "All", "Only on confirmed", "Only on rejected"]}
                                ids={["none", "all", "only_confirmed", "only_rejected"]}
                                selected={endpoint_require_confirmation_example_tags}
                                onChange={(type) => { setIsInit(false); setEndpointRequireConfirmationExampleTags(type as EndpointRequireConfirmationExampleTags) }} />
                        </div>
                    </div>}
                    {endpoint_require_confirmation !== "never" && endpoint_template_onboarding !== undefined && <Fragment>
                        <label htmlFor="endpoint_automatic_template_example" className="block text-sm font-medium sm:col-span-2 leading-6 text-gray-900 sm:pt-1.5">
                            <p>Tags for Confirmed Examples</p>
                        </label>
                        <div className="mt-2 sm:mt-0 sm:col-span-2 flex flex-row gap-x-2 items-center">
                            <div className="flex-grow">
                                <MultiselectInputField
                                    values={endpoint_automatic_template_example_tags.confirm}
                                    placeholder="No automatic tags"
                                    separator="space"
                                    color="green"
                                    onRemove={(tag_idx) => { setIsInit(false); removeAutomaticTemplateExampleTag("confirm", tag_idx) }}
                                />
                            </div>
                            <DropdownMenu
                                title="Tags"
                                items={green_tags.map((tag) => ({ title: tag, onClick: () => { setIsInit(false); addAutomaticTemplateExampleTag("confirm", tag); } }))}
                                align="right"
                            />
                        </div>
                        <label htmlFor="endpoint_automatic_template_example" className="block text-sm font-medium sm:col-span-2 leading-6 text-gray-900 sm:pt-1.5">
                            <p>Tags for Rejected Examples</p>
                        </label>
                        <div className="mt-2 sm:mt-0 sm:col-span-2 flex flex-row gap-x-2 items-center">
                            <div className="flex-grow">
                                <MultiselectInputField
                                    values={endpoint_automatic_template_example_tags.reject}
                                    placeholder="No automatic tags"
                                    separator="space"
                                    color="red"
                                    onRemove={(tag_idx) => { setIsInit(false); removeAutomaticTemplateExampleTag("reject", tag_idx) }}
                                />
                            </div>
                            <DropdownMenu
                                title="Tags"
                                items={red_tags.map((tag) => ({ title: tag, onClick: () => { setIsInit(false); addAutomaticTemplateExampleTag("reject", tag); } }))}
                                align="right"
                            />
                        </div>
                    </Fragment>}
                </div>
                <div className="sm:grid sm:grid-cols-4 sm:items-start sm:gap-4 py-6 border-b border-gray-900/10">
                    <label htmlFor="endpoint_automatic_template_example" className="block text-sm font-medium sm:col-span-3 leading-6 text-gray-900 sm:pt-1.5">
                        <p>Automatically Create Template Examples</p>
                        <p className="mt-3 max-w-2xl text-sm leading-6 font-normal text-gray-600">
                            Processed items will be automatically included as examples for the template.
                        </p>
                    </label>
                    <div className="mt-2 sm:mt-0">
                        <Dropdown
                            values={["No", "Yes", "Only on error"]}
                            ids={["never", "always", "on_error"]}
                            selected={endpoint_automatic_template_example}
                            onChange={(type) => { setIsInit(false); setEndpointAutomaticTemplateExample(type as EndpointRequireConfirmation) }} />
                    </div>
                    {endpoint_automatic_template_example === "always" && <Fragment>
                        <label htmlFor="endpoint_automatic_template_example" className="block text-sm font-medium sm:col-span-2 leading-6 text-gray-900 sm:pt-1.5">
                            <p>Tags for Automatic Examples</p>
                        </label>
                        <div className="mt-2 sm:mt-0 sm:col-span-2 flex flex-row gap-x-2 items-center">
                            <div className="flex-grow">
                                <MultiselectInputField
                                    values={endpoint_automatic_template_example_tags.always}
                                    placeholder="No automatic tags"
                                    separator="space"
                                    color="sky"
                                    onRemove={(tag_idx) => { setIsInit(false); removeAutomaticTemplateExampleTag("always", tag_idx); }}
                                />
                            </div>
                            <DropdownMenu
                                title="Tags"
                                items={blue_tags.map((tag) => ({ title: tag, onClick: () => { setIsInit(false); addAutomaticTemplateExampleTag("always", tag); } }))}
                                align="right"
                            />
                        </div>
                    </Fragment>}
                </div>
                <div className="sm:grid sm:grid-cols-6 sm:items-start sm:gap-4 py-6">
                    <label htmlFor="endpoint_store_extractions" className="block text-sm font-medium sm:col-span-4 leading-6 text-gray-900 sm:pt-1.5">
                        <p>Store Results</p>
                        <p className="mt-3 max-w-2xl text-sm leading-6 font-normal text-gray-600">
                            Extract-and-Forget when not enabled. If enabled, all data and results will be stored in the database.
                        </p>
                    </label>
                    <div className="mt-2 sm:mt-0 sm:col-span-2">
                        <Checkbox
                            id="endpoint_store_extractions"
                            checked={endpoint_store_extractions}
                            setChecked={(checked) => { setIsInit(false); setEndpointStoreExtractions(checked); }}
                        />
                    </div>

                    {endpoint_type === ENDPOINT_TYPE.email && <Fragment>
                        <label htmlFor="endpoint_unit_of_processing"
                            className="block sm:col-span-4 text-sm font-medium leading-6 sm:pt-1.5 text-gray-900">
                            <p>Email input handling</p>
                            <p className="mt-3 max-w-2xl text-sm leading-6 font-normal text-gray-600">
                                How to handle email input. If you want to process only attachments, set it to "Only attachments".
                            </p>
                        </label>
                        <div className="mt-2 sm:mt-0 sm:col-span-2">
                            <Dropdown
                                values={[
                                    "Everything",
                                    "Only attachments (use email body when no attachments)",
                                    "Only attachments (skip whole email when no attachments)",
                                    "Only email body"
                                ]}
                                ids={["all", "only_attachments_fallback_to_email_body", "only_attachments", "only_email_body"]}
                                selected={endpoint_email_input_handling}
                                onChange={(handling) => { setIsInit(false); setEndpointEmailInputHandling(handling as EndpointEmailInputHandling); }} />
                        </div>
                    </Fragment>}

                    <label htmlFor="endpoint_item_name"
                        className={classNames("block text-sm font-medium sm:col-span-2 leading-6 sm:pt-1.5 text-gray-900")}>
                        <p>Custom result name</p>
                        <p className="mt-3 max-w-2xl text-sm leading-6 font-normal text-gray-600">
                            By default result are named after input (email subject, filename, etc.). You can change it here to contain the
                            values from the result like PO number, invoice number, etc.
                        </p>
                    </label>
                    <div className="mt-2 sm:mt-0">
                        <Checkbox
                            id="endpoint_item_name"
                            aria-describedby="endpoint_item_name"
                            checked={endpoint_item_name !== undefined}
                            setChecked={(checked) => { setIsInit(false); if (checked) { setEndpointItemName([]); } else { setEndpointItemName(undefined); } }}
                        />
                    </div>
                    {endpoint_item_name && <div className="mt-2 sm:mt-0 sm:col-span-3">
                        <EndpointOutputName
                            title={"Item name"}
                            contexts={endpoint_contexts}
                            split_fields_data={[]}
                            elements={endpoint_item_name}
                            placeholder="Use default item name"
                            separator="space"
                            addElement={addItemNameElement}
                            removeElement={removeItemNameElement} />
                    </div>}

                    {(endpoint_type !== "email" || endpoint_email_input_handling === "only_attachments" || endpoint_email_input_handling === "only_attachments_fallback_to_email_body") && <Fragment>
                        <label htmlFor="endpoint_unit_of_processing"
                            className="block sm:col-span-4 text-sm font-medium leading-6 sm:pt-1.5 text-gray-900">
                            <p>Unit of processing</p>
                            <p className="mt-3 max-w-2xl text-sm leading-6 font-normal text-gray-600">
                                Useful for processing multiple documents or larger documents where each file or file/page/sheet is a separate entity.
                            </p>
                        </label>
                        <div className="mt-2 sm:mt-0 sm:col-span-2">
                            <Dropdown
                                values={[
                                    "All files together",
                                    "Each file separately",
                                    "Each section of pages separately",
                                    "Each page/sheet separately"
                                ]}
                                ids={["all", "file", "section", "page"]}
                                selected={endpoint_unit_of_processing}
                                onChange={(unit) => { setIsInit(false); setEndpointUnitOfProcessing(unit as UnitOfProcessing); }} />
                        </div>

                        {endpoint_unit_of_processing === "section" && <Fragment>
                            <label htmlFor="endpoint_guidelines" className="block sm:col-span-6 text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                                <p>Guidelines for section detection (optional)</p>
                            </label>
                            <div className="sm:col-span-6">
                                <Textarea
                                    id="endpoint_automatic_doc_boundary_detection_guidelines"
                                    value={endpoint_automatic_doc_boundary_detection_guidelines}
                                    placeholder="Describe how the document should be sectioned (e.g., 'Split by chapter headers', 'Separate each invoice')"
                                    onChange={(value) => {
                                        setIsInit(false);
                                        setEndpointAutomaticDocBoundaryDetectionGuidelines(value);
                                    }}
                                />
                            </div>
                        </Fragment>}
                    </Fragment>}

                    {endpoint_type === ENDPOINT_TYPE.email && endpoint_email_input_handling !== "only_email_body" && <Fragment>
                        <label htmlFor="endpoint_attachment_filename_regex"
                            className="block sm:col-span-4 text-sm font-medium leading-6 sm:pt-1.5 text-gray-900">
                            <p>Attachment filename regex</p>
                            <p className="mt-3 max-w-2xl text-sm leading-6 font-normal text-gray-600">
                                Regex to filter attachments by filename. Empty means all attachments will be processed.
                            </p>
                        </label>
                        <div className="mt-2 sm:mt-0 sm:col-span-2">
                            <Textbox
                                id="endpoint_attachment_filename_regex"
                                value={endpoint_attachment_filename_regex}
                                onChange={(value) => { setIsInit(false); setEndpointAttachmentFilenameRegex(value); }}
                            />
                        </div>
                    </Fragment>}
                </div>

                {show_admin_details && is_admin && <div className="sm:grid sm:grid-cols-4 max-w-5xl sm:items-start sm:gap-4 sm:pt-6 border-t border-gray-900/10 text-sm font-bold leading-6 text-gray-400">
                    Admin settings
                </div>}

                {show_admin_details && is_admin && <div className="sm:grid sm:grid-cols-6 sm:items-start sm:gap-4 py-3">
                    <label htmlFor="endpoint_truncate_long_text" className="block sm:col-span-2 text-sm font-medium leading-6 text-gray-400 sm:pt-1.5">
                        <p>Truncate long text</p>
                    </label>
                    <div className="mt-2 sm:mt-0">
                        <Checkbox
                            id="endpoint_truncate_long_text"
                            checked={endpoint_truncate_long_text}
                            setChecked={(checked) => { setIsInit(false); setEndpointTruncateLongText(checked) }}
                        />
                    </div>
                </div>}

                {!show_admin_details && is_admin && <div className="px-4 py-6 border-t border-gray-200">
                    <Button text="Show admin details" onClick={() => setShowAdminDetails(true)} icon={hi.ChevronDownIcon} />
                </div>}

                {show_admin_details && is_admin && <div className="px-4 py-6 border-t border-gray-200">
                    <Button text="Hide admin details" onClick={() => setShowAdminDetails(false)} icon={hi.ChevronUpIcon} />
                </div>}
            </form >
        </div >}
    </div >;
}
